import posthog from 'posthog-js';
import imei from 'imei';
import { useCallback, useEffect } from 'react';

import { useNavigator } from '../hooks/useNavigator';
import useTranslation from '../hooks/useTranslation';
import { useStore } from '../hooks/useStore';
import GradingForm from '../components/GradingForm';
import SelectedDevice from '../components/SelectedDevice';
import HowtoLink from '../components/HowtoLink';
import DeviceIdentifierTextInput from '../components/DeviceIdentifierTextInput';
import { DeviceType } from '@/lib/constants/foxway';
import StepTemplate from '../components/StepTemplate';
import { Button } from '@/lib/components/ui/button';
import { SpinnerButton } from '@/lib/components/ui/spinnerButton';

const GradingStep = () => {
  const translations = useTranslation();

  const navigateBack = useNavigator((state) => state.navigateBack);
  const navigateForward = useNavigator((state) => state.navigateForward);
  const partner = useStore((state) => state.partner);
  const loadingPriceQuote = useStore((state) => state.loadingPriceQuote);
  const selectedDeviceType = useStore((state) => state.selectedDeviceType);
  const modelsQuery = useStore((state) => state.modelsQuery);
  const setDeviceIdentifier = useStore((state) => state.setDeviceIdentifier);
  const gradingForm = useStore((state) => state.gradingForm);
  const gradingAnswers = useStore((state) => state.gradingAnswers);
  const gradingExtras = useStore((state) => state.gradingExtras);
  const deviceIdentifierValid = useStore(
    (state) => state.deviceIdentifierValid,
  );
  const validateDeviceIdentifier = useStore(
    (state) => state.validateDeviceIdentifier,
  );
  const validatingDeviceIdentifier = useStore(
    (state) => state.validatingDeviceIdentifier,
  );
  const getPriceQuote = useStore((state) => state.getPriceQuote);

  const answersCompleted =
    !gradingForm?.Questions?.length ||
    (gradingAnswers &&
      Object.keys(gradingAnswers).length === gradingForm?.Questions.length);

  const extrasCompleted =
    !gradingForm?.Extras?.length ||
    (gradingExtras &&
      Object.keys(gradingExtras).length === gradingForm?.Extras.length);

  const readyForPriceQuote = answersCompleted && extrasCompleted;
  const delayedRegistration = partner?.featureToggles.delayedRegistration;

  useEffect(() => {
    posthog.capture('Open Grading Step');
  }, []);

  useEffect(() => {
    if (selectedDeviceType === DeviceType.mobile && imei.isValid(modelsQuery)) {
      // If the user has entered a valid IMEI in models query, we set it as the device identifier
      setDeviceIdentifier(modelsQuery);
      validateDeviceIdentifier();
    }
  }, [
    selectedDeviceType,
    modelsQuery,
    setDeviceIdentifier,
    validateDeviceIdentifier,
  ]);

  useEffect(() => {
    if (readyForPriceQuote) {
      getPriceQuote();
    }
  }, [gradingAnswers, gradingExtras, readyForPriceQuote]);

  const onNext = useCallback(async () => {
    if (
      !delayedRegistration &&
      !deviceIdentifierValid &&
      !(await validateDeviceIdentifier())
    ) {
      return;
    }

    await getPriceQuote();

    navigateForward();
  }, [
    deviceIdentifierValid,
    validateDeviceIdentifier,
    getPriceQuote,
    navigateForward,
  ]);
  return (
    <StepTemplate
      title={translations.grading}
      subTitle={translations.gradingTitle}
      step={2}
      footer={
        <div className="flex space-x-4">
          <Button
            variant={'outline'}
            onClick={navigateBack}
            aria-label={translations['back']}
          >
            {translations['back']}
          </Button>
          <SpinnerButton
            onClick={onNext}
            aria-label={translations['next']}
            disabled={!answersCompleted || !extrasCompleted}
            loading={validatingDeviceIdentifier || loadingPriceQuote}
          >
            {translations['next']}
          </SpinnerButton>
        </div>
      }
    >
      <div className={'bg-white px-4 py-2 sm:px-10 sm:py-7 rounded-lg'}>
        <GradingForm />
        {!delayedRegistration && <DeviceIdentifierTextInput />}
        <HowtoLink />
        <SelectedDevice />
      </div>
    </StepTemplate>
  );
};

export default GradingStep;
